<template>
  <Toast />
  <ConfirmPopup></ConfirmPopup>
  <div class="subscriber-view">
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <h1 class="title">Acquired Data Offers</h1>
          <span class="catalog-badge">Data Catalog</span>
        </div>
      </div>
    </div>

    <section class="subscription-section">
      <div :class="{ 'subscription-card': assetsFromGlobalCatalog.length > 0 }">
        <div v-if="assetsFromGlobalCatalog.length === 0" class="empty-category">
          <div class="empty-icon">🔍</div>
          <h4>No matching assets</h4>
          <p>Try adjusting your filters to see results.</p>
        </div>
        <div
          v-else
          v-for="asset in assetsFromGlobalCatalog"
          :key="asset.id"
          class="pull-asset-card"
        >
          <div class="pull-asset-body">
            <div
              class="pull-title-row"
              style="
                display: flex;
                align-items: center;
                justify-content: space-between;
              "
            >
              <h4>{{ asset.name || asset.title }}</h4>
              <button
                v-if="asset"
                class="delete-icon"
                @click="confirm2($event, asset)"
                aria-label="Delete asset"
              >
                <svg viewBox="0 0 24 24">
                  <path
                    d="M3 6h18M8 6V4h8v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div class="policy-card-summary">
              <template v-if="hasAssetPolicySummary(asset)">
                <div
                  v-for="section in getAssetCardPolicySections(asset)"
                  :key="section.type"
                  class="policy-card-section"
                >
                  <div class="policy-card-section-label">
                    <span
                      :class="['policy-rule-dot', section.className]"
                    ></span>
                    {{ section.label }}
                  </div>
                  <div class="policy-card-pills">
                    <span
                      v-for="(constraint, ci) in section.constraints"
                      :key="ci"
                      :class="[
                        'policy-card-pill',
                        constraint === 'N/A N/A N/A'
                          ? 'policy-card-pill--prohibition'
                          : `policy-card-pill--${section.type}`,
                      ]"
                    >
                      {{
                        constraint === "N/A N/A N/A"
                          ? "No constraints"
                          : constraint
                      }}
                    </span>
                    <span
                      v-if="section.duties && section.duties.length > 0"
                      :class="[
                        'policy-card-pill',
                        `policy-card-pill--${section.type}`,
                      ]"
                    >
                      Duties: {{ section.duties.length }}
                    </span>
                  </div>
                </div>
              </template>
              <p v-else class="policy-card-description">
                {{ asset.description || "No description provided." }}
              </p>
            </div>
            <div class="pull-chips">
              <span v-for="tag in asset.keywords" :key="tag" class="chip">
                {{ tag }}
              </span>
              <span v-for="tag in asset.theme" :key="tag" class="chip ghost">
                {{ tag }}
              </span>
            </div>
            <div
              style="
                padding: 30px 0 30px 0;
                display: flex;
                gap: 10px;
                width: 100%;
                justify-content: flex-end;
              "
            >
              <button
                class="btn btn--primary"
                @click="toggleAssetDetails(asset)"
              >
                Details
              </button>
              <button
                class="btn btn--primary"
                @click="openNegotiatedPolicyModal(asset)"
              >
                Data Offers
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <!-- Modal -->
  <Dialog
    v-model:visible="showAssetModal"
    modal
    :header="modalTitle"
    :style="{ width: '70%', background: '#f5f7fb' }"
  >
    <div class="modal__content">
      <div class="modal-toolbar">
        <Button
          v-if="canGoBackInModal"
          label="Back"
          icon="pi pi-arrow-left"
          severity="secondary"
          text
          @click="goBackInModal"
        />
      </div>

      <div
        v-if="modalView === 'similar'"
        :class="{ 'subscription-card': modalView !== 'similar' }"
      >
        <div v-if="similarAssets.length === 0" class="empty-category">
          <div class="empty-icon">🔍</div>
          <h4>No matching assets</h4>
          <p>Try adjusting your filters to see results.</p>
        </div>
        <div
          v-else
          v-for="asset in similarAssets"
          :key="asset.id || asset['@id']"
          class="pull-asset-card"
        >
          <div class="pull-asset-body">
            <div class="pull-title-row">
              <h4>{{ asset.name }}</h4>
              <span class="pull-tag">{{ asset.id }}</span>
            </div>
            <p>
              {{ asset.description || "No description provided." }}
            </p>
            <div class="pull-meta">
              <span>Endpoint: {{ asset.endpoint }}</span>
              <span v-if="asset.participantId">
                Participant: {{ asset.participantId }}
              </span>
            </div>
            <div class="pull-chips">
              <span v-for="tag in asset.keywords" :key="tag" class="chip">
                {{ tag }}
              </span>
              <span v-for="tag in asset.theme" :key="tag" class="chip ghost">
                {{ tag }}
              </span>
            </div>
            <div
              style="
                padding: 30px 0 30px 0;
                display: flex;
                gap: 10px;
                width: 100%;
                justify-content: flex-end;
              "
            >
              <button
                class="btn btn--primary"
                @click="toggleSelectedAssetDetails(asset)"
              >
                Details
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="selectedAsset"
        class="asset-details asset-details--accordion"
      >
        <details class="detail-accordion" open>
          <summary>Basic Information</summary>
          <div class="detail-panel">
            <div class="detail-grid">
              <div class="detail-item">
                <label>Asset ID:</label>
                <span>{{
                  selectedAsset["@id"] || selectedAsset.id || "N/A"
                }}</span>
              </div>
              <div class="detail-item">
                <label>Asset Name:</label>
                <span>{{
                  selectedAsset.name || selectedAsset.title || "N/A"
                }}</span>
              </div>
              <div class="detail-item">
                <label>Type:</label>
                <span>{{
                  selectedAsset.type ||
                  selectedAsset.dataAddress?.type ||
                  "Data Asset"
                }}</span>
              </div>
              <div class="detail-item">
                <label>Description:</label>
                <span>{{
                  selectedAsset.description || "No description available"
                }}</span>
              </div>
              <div class="detail-item" v-if="selectedAsset.AssetVersion">
                <label>Asset Version:</label>
                <span>{{ selectedAsset.AssetVersion }}</span>
              </div>
            </div>
          </div>
        </details>

        <details class="detail-accordion">
          <summary>Connector Information</summary>
          <div class="detail-panel">
            <div class="detail-grid">
              <div class="detail-item">
                <label>Catalog Name:</label>
                <span>{{ selectedAsset.catalog_name || "N/A" }}</span>
              </div>
              <div class="detail-item">
                <label>Endpoint:</label>
                <span>{{ selectedAsset.endpoint || "N/A" }}</span>
              </div>
              <div class="detail-item">
                <label>DID:</label>
                <span>{{ selectedAsset.did || "N/A" }}</span>
              </div>
              <div class="detail-item" v-if="selectedAsset.format">
                <label>Format:</label>
                <span>{{ selectedAsset.format || "N/A" }}</span>
              </div>
            </div>
          </div>
        </details>

        <details class="detail-accordion" v-if="selectedAsset.pricingPlan">
          <summary>Pricing Plan</summary>
          <div class="detail-panel">
            <div class="detail-grid">
              <div class="detail-item">
                <label>Price:</label>
                <span>{{ selectedAsset.pricingPlan.price || "N/A" }}</span>
              </div>
              <div class="detail-item">
                <label>Type:</label>
                <span>{{ selectedAsset.pricingPlan.unit || "N/A" }}</span>
              </div>
              <div class="detail-item">
                <label>Currency:</label>
                <span>{{
                  selectedAsset.pricingPlan.priceCurrency || "N/A"
                }}</span>
              </div>
            </div>
          </div>
        </details>

        <details class="detail-accordion" v-if="selectedAsset.CreatorInfo">
          <summary>Creator Information</summary>
          <div class="detail-panel">
            <div class="detail-grid">
              <div class="detail-item">
                <label>Creator Name:</label>
                <span>{{ selectedAsset.CreatorInfo.legalName || "N/A" }}</span>
              </div>
              <div class="detail-item">
                <label>Contact Name:</label>
                <span>{{
                  selectedAsset.CreatorInfo.contactName || "N/A"
                }}</span>
              </div>
              <div class="detail-item">
                <label>Email:</label>
                <span>{{ selectedAsset.CreatorInfo.email || "N/A" }}</span>
              </div>
              <div class="detail-item">
                <label>URL:</label>
                <span>{{ selectedAsset.CreatorInfo.url || "N/A" }}</span>
              </div>
              <div class="detail-item" v-if="selectedAsset.AssetVersion">
                <label>ID:</label>
                <span>{{ selectedAsset.CreatorInfo.uid }}</span>
              </div>
            </div>
          </div>
        </details>

        <details
          class="detail-accordion"
          v-if="selectedAsset.properties?.attributes"
        >
          <summary>Field Attributes</summary>
          <div class="detail-panel">
            <div class="attributes-container">
              <div v-if="Array.isArray(selectedAsset.properties.attributes)">
                <div
                  v-for="(attribute, index) in selectedAsset.properties
                    .attributes"
                  :key="index"
                  class="attribute-card"
                >
                  <div class="attribute-header">
                    <h4 class="field-name">
                      {{ attribute.fieldName || "Unnamed Field" }}
                    </h4>
                    <span class="data-type">{{
                      attribute.dataType || "Unknown Type"
                    }}</span>
                  </div>
                  <div class="attribute-details">
                    <div class="detail-row">
                      <span class="detail-label">Description:</span>
                      <span class="detail-value">{{
                        attribute.description || "No description"
                      }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Size:</span>
                      <span class="detail-value">{{
                        attribute.size || "N/A"
                      }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Value:</span>
                      <span class="detail-value">{{
                        attribute.value || "N/A"
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </details>
      </div>
      <div v-else class="loading-state">
        <p>Loading asset details...</p>
      </div>
    </div>
    <template #footer>
      <div class="modal-footer">
        <button
          v-if="modalView === 'details' && selectedAsset"
          class="btn btn--primary"
          @click="openSimilarAssets(selectedAsset)"
        >
          Similar Assets
        </button>
        <button
          v-if="modalView === 'details' && selectedAsset"
          class="btn btn--primary"
          @click="subscribeEndpoint(selectedAsset)"
        >
          Subscribe Endpoint
        </button>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showPolicyDialog"
    modal
    header="Policy Details"
    :style="{ width: '58rem', maxWidth: '92vw', background: '#f5f7fb' }"
  >
    <div class="policy-preview-dialog" v-if="selectedPolicyForPreview">
      <div class="policy-preview-dialog__header">
        <div>
          <span>Selected Policy</span>
          <h3>Policy Preview</h3>
        </div>
      </div>
      <pre>{{ formattedSelectedPolicy }}</pre>
    </div>
  </Dialog>

  <Dialog
    v-model:visible="showNegotiatedPolicyModal"
    modal
    header="Negotiated Policy"
    :style="{ width: '70%', background: '#f5f7fb' }"
  >
    <div class="subscription-section">
      <div :class="{ 'subscription-card': negotiatedPolicyAssets.length > 0 }">
        <div v-if="negotiatedPolicyAssets.length === 0" class="empty-category">
          <div class="empty-icon">🔍</div>
          <h4>No negotiated policies</h4>
          <p>There are no negotiated policy offers for this asset.</p>
        </div>
        <div
          v-else
          v-for="asset in negotiatedPolicyAssets"
          :key="asset.id"
          class="pull-asset-card"
        >
          <div class="pull-asset-body">
            <div class="pull-title-row">
              <h4>{{ asset.name || asset.title }}</h4>
              <div
                v-if="Array.isArray(asset.policy)"
                class="btn btn--secondary"
                style="margin-left: auto"
              >
                Negotiated Policy
              </div>
              <div v-else class="btn btn--secondary" style="margin-left: auto">
                Original Policy
              </div>
            </div>
            <div class="policy-card-summary">
              <template v-if="hasAssetPolicySummary(asset)">
                <div
                  v-for="section in getAssetCardPolicySections(asset)"
                  :key="section.type"
                  class="policy-card-section"
                >
                  <div class="policy-card-section-label">
                    <span
                      :class="['policy-rule-dot', section.className]"
                    ></span>
                    {{ section.label }}
                  </div>
                  <div class="policy-card-pills">
                    <span
                      v-for="(constraint, ci) in section.constraints"
                      :key="ci"
                      :class="[
                        'policy-card-pill',
                        `policy-card-pill--${section.type}`,
                      ]"
                    >
                      {{ constraint }}
                    </span>
                  </div>
                </div>
              </template>
              <p v-else class="policy-card-description">
                {{ asset.description || "No description provided." }}
              </p>
            </div>
            <div class="pull-chips">
              <span v-for="tag in asset.keywords" :key="tag" class="chip">
                {{ tag }}
              </span>
              <span v-for="tag in asset.theme" :key="tag" class="chip ghost">
                {{ tag }}
              </span>
            </div>
            <div
              style="
                padding: 30px 0 30px 0;
                display: flex;
                gap: 10px;
                width: 100%;
                justify-content: flex-end;
              "
            >
              <button
                class="btn btn--primary"
                @click="toggleSelectedAssetDetails(asset)"
              >
                Details
              </button>
              <button
                class="btn btn--primary"
                @click="$emit('request-access', asset)"
              >
                Request Access
              </button>
              <button
                v-if="Array.isArray(asset.policy)"
                class="delete-icon"
                @click="confirm3($event, asset)"
                aria-label="Delete asset"
              >
                <svg viewBox="0 0 24 24">
                  <path
                    d="M3 6h18M8 6V4h8v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>

  <Dialog
    v-model:visible="showSelectedAssetDetails"
    modal
    :header="modalTitle"
    :style="{ width: '70%', background: '#f5f7fb' }"
  >
    <div class="asset-details asset-details--accordion">
      <details class="detail-accordion" open>
        <summary>Policy Information</summary>
        <div class="detail-panel">
          <div
            v-if="
              normalizePolicyArray(selectedAssetDetails?.policy).filter(Boolean)
            "
            class="policy-grid"
          >
            <article
              v-for="(policy, index) in normalizePolicyArray(
                selectedAssetDetails?.policy
              )"
              :key="getPolicyKey(policy, index)"
              class="policy-modern-card"
            >
              <div class="policy-modern-card__header">
                <div class="policy-modern-card__icon">
                  <i class="pi pi-shield"></i>
                </div>
                <div class="policy-modern-card__title">
                  <h4>{{ getPolicyDisplayName(index) }}</h4>
                </div>
              </div>

              <div
                v-if="policy.modifiedAt || policy.negotiatedPolicy"
                class="policy-status-strip"
              >
                <div v-if="policy.modifiedAt" class="policy-status-time">
                  <i class="pi pi-clock"></i>
                  <div>
                    <span>Modified</span>
                    <strong>{{
                      new Date(policy.modifiedAt).toLocaleString()
                    }}</strong>
                  </div>
                </div>
                <span
                  v-if="policy.negotiatedPolicy"
                  class="policy-negotiated-badge"
                >
                  <i class="pi pi-check-circle"></i>
                  Negotiated policy
                </span>
              </div>

              <div v-if="policyHasRules(policy)" class="policy-rule-list">
                <section
                  v-for="section in policyRuleSections"
                  :key="section.key"
                  v-show="hasData(getPolicySectionData(policy, section.key))"
                  class="policy-rule-section"
                >
                  <div class="policy-rule-section__heading">
                    <span
                      :class="['policy-rule-dot', section.className]"
                    ></span>
                    {{ section.label }}
                  </div>

                  <div
                    v-for="(rule, ruleIndex) in normalizePolicyArray(
                      getPolicySectionData(policy, section.key)
                    )"
                    :key="`${section.key}-${ruleIndex}`"
                    class="policy-rule"
                  >
                    <div
                      v-for="(
                        constraint, constraintIndex
                      ) in getPolicyConstraints(rule)"
                      :key="constraintIndex"
                      class="policy-constraint"
                    >
                      <div class="policy-constraint-row">
                        <span>Left Operand</span>
                        <strong>{{
                          formatPolicyValue(
                            getPolicyFieldValue(constraint, "leftOperand", rule)
                          )
                        }}</strong>
                      </div>
                      <div class="policy-constraint-row">
                        <span>Operator</span>
                        <strong>{{
                          formatPolicyValue(
                            getPolicyFieldValue(constraint, "operator", rule)
                          )
                        }}</strong>
                      </div>
                      <div class="policy-constraint-row">
                        <span>Right Operand</span>
                        <strong>{{
                          formatPolicyValue(
                            getPolicyFieldValue(
                              constraint,
                              "rightOperand",
                              rule
                            )
                          )
                        }}</strong>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <div v-else class="policy-empty-state">
                No permission, prohibition, or obligation rules found.
              </div>

              <div class="policy-modern-card__actions">
                <button
                  class="policy-action policy-action--primary"
                  @click="viewPolicy(policy, selectedAssetDetails)"
                >
                  View Policy
                </button>
              </div>
            </article>
          </div>

          <div v-else class="policy-empty-state">
            No policy information available.
          </div>
        </div>
      </details>

      <details class="detail-accordion">
        <summary>Basic Information</summary>
        <div class="detail-panel">
          <div class="detail-grid">
            <div class="detail-item">
              <label>Asset ID:</label>
              <span>{{
                selectedAssetDetails["@id"] || selectedAssetDetails.id || "N/A"
              }}</span>
            </div>
            <div class="detail-item">
              <label>Asset Name:</label>
              <span>{{
                selectedAssetDetails.name || selectedAssetDetails.title || "N/A"
              }}</span>
            </div>
            <div class="detail-item">
              <label>Type:</label>
              <span>{{
                selectedAssetDetails.type ||
                selectedAssetDetails.dataAddress?.type ||
                "Data Asset"
              }}</span>
            </div>
            <div class="detail-item">
              <label>Description:</label>
              <span>{{
                selectedAssetDetails.description || "No description available"
              }}</span>
            </div>
            <div class="detail-item" v-if="selectedAssetDetails.AssetVersion">
              <label>Asset Version:</label>
              <span>{{ selectedAssetDetails.AssetVersion }}</span>
            </div>
          </div>
        </div>
      </details>

      <details class="detail-accordion">
        <summary>Connector Information</summary>
        <div class="detail-panel">
          <div class="detail-grid">
            <div class="detail-item">
              <label>Catalog Name:</label>
              <span>{{ selectedAssetDetails.catalog_name || "N/A" }}</span>
            </div>
            <div class="detail-item">
              <label>Endpoint:</label>
              <span>{{ selectedAssetDetails.endpoint || "N/A" }}</span>
            </div>
            <div class="detail-item">
              <label>DID:</label>
              <span>{{ selectedAssetDetails.did || "N/A" }}</span>
            </div>
            <div class="detail-item" v-if="selectedAssetDetails.format">
              <label>Format:</label>
              <span>{{ selectedAssetDetails.format || "N/A" }}</span>
            </div>
          </div>
        </div>
      </details>

      <details class="detail-accordion" v-if="selectedAssetDetails.pricingPlan">
        <summary>Pricing Plan</summary>
        <div class="detail-panel">
          <div class="detail-grid">
            <div class="detail-item">
              <label>Price:</label>
              <span>{{ selectedAssetDetails.pricingPlan.price || "N/A" }}</span>
            </div>
            <div class="detail-item">
              <label>Type:</label>
              <span>{{ selectedAssetDetails.pricingPlan.unit || "N/A" }}</span>
            </div>
            <div class="detail-item">
              <label>Currency:</label>
              <span>{{
                selectedAssetDetails.pricingPlan.priceCurrency || "N/A"
              }}</span>
            </div>
          </div>
        </div>
      </details>

      <details class="detail-accordion" v-if="selectedAssetDetails.CreatorInfo">
        <summary>Creator Information</summary>
        <div class="detail-panel">
          <div class="detail-grid">
            <div class="detail-item">
              <label>Creator Name:</label>
              <span>{{
                selectedAssetDetails.CreatorInfo.legalName || "N/A"
              }}</span>
            </div>
            <div class="detail-item">
              <label>Contact Name:</label>
              <span>{{
                selectedAssetDetails.CreatorInfo.contactName || "N/A"
              }}</span>
            </div>
            <div class="detail-item">
              <label>Email:</label>
              <span>{{ selectedAssetDetails.CreatorInfo.email || "N/A" }}</span>
            </div>
            <div class="detail-item">
              <label>URL:</label>
              <span>{{ selectedAssetDetails.CreatorInfo.url || "N/A" }}</span>
            </div>
            <div class="detail-item" v-if="selectedAssetDetails.AssetVersion">
              <label>ID:</label>
              <span>{{ selectedAssetDetails.CreatorInfo.uid }}</span>
            </div>
          </div>
        </div>
      </details>
    </div>
    <template #footer>
      <div class="modal-footer">
        <button
          class="btn btn--primary"
          @click="acquireAsset(selectedAssetDetails)"
        >
          Acquire Data Offer
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, computed, watch } from "vue";
import axios from "axios";
import { useConfirm } from "primevue/useconfirm";
import ConfirmPopup from "primevue/confirmpopup";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { userService } from "../services/UserService";
import Button from "primevue/button";
import Dialog from "primevue/dialog";

const toast = useToast();

const { appContext } = getCurrentInstance();
const API_BASE_URL = appContext.config.globalProperties.$apiBase;

const props = defineProps({
  user: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["refresh-catalogs", "request-access"]);

const confirm = useConfirm();

const newCatalogName = ref("");
const newCatalogEndpoint = ref("");
const newCatalogCategory = ref("");
const newCatalogProtocol = ref("");
const newCatalogDescription = ref("");
const token = userService.getCookie("ZDZW_cookie");
const user = userService.getCookieUser().organization[0];
const pullAssets = ref(false);
const assetsFromGlobalCatalog = ref([]);
const selectedAsset = ref(null);
const showAssetModal = ref(false);
const modalView = ref("details");
const modalHistory = ref([]);
const similarAssets = ref([]);
const showSelectedAssetDetails = ref(false);
const selectedAssetDetails = ref([]);
const showPolicyDialog = ref(false);
const selectedPolicyForPreview = ref(null);
const showNegotiatedPolicyModal = ref(false);
const negotiatedPolicyAssets = ref([]);
const originalPolicyAssets = ref([]);

const catalogs = ref([]);
const portalCatalogs = ref([]);
const selectedPortalCatalogId = ref("");

const showFieldPopup = ref(false);
const selectedCatalog = ref(null);
const schemaFields = ref([]);
const objectId = ref("");
const defaultValueExist = ref(false);
const fieldDefaults = ref({});
const fieldInputs = ref({});

const modalTitle = computed(() => {
  if (modalView.value === "similar") return "Similar Assets";
  return "Asset Details";
});

const canGoBackInModal = computed(() => modalHistory.value.length > 0);

const policyRuleSections = [
  {
    key: "permission",
    label: "Permissions",
    className: "policy-rule-dot--permission",
  },
  {
    key: "prohibition",
    label: "Prohibitions",
    className: "policy-rule-dot--prohibition",
  },
  {
    key: "obligation",
    label: "Obligations",
    className: "policy-rule-dot--obligation",
  },
];

const normalizedSelectedAssetPolicies = computed(() => {
  return normalizePolicyArray(selectedAsset.value?.policy).filter(Boolean);
});

const formattedSelectedPolicy = computed(() => {
  if (!selectedPolicyForPreview.value) return "";
  return JSON.stringify(selectedPolicyForPreview.value, null, 2);
});

const onToggle = (event, catalog) => {
  console.log("Panel toggled:", event);
  console.log("Collapsed?", event.value); // true = collapsed, false = expanded
  togglePullCatalog(catalog);
};

const inferFieldType = (field) => {
  const fieldName = field?.name || "";
  if (/^(is|has)[A-Z_]/.test(fieldName)) return "boolean";
  if (/score|count|total|amount|number|size|price/i.test(fieldName))
    return "number";
  return "text";
};

const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

const fetchPortalCatalogs = async () => {
  try {
    const response = await axios.get(
      "https://portal-api.ds2.icelab.cloud/organization/catalog",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    portalCatalogs.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Error fetching portal catalogs:", error);
    toast.add({
      severity: "error",
      summary: "Portal Error",
      detail: "Unable to load portal catalogs.",
      life: 2500,
    });
  }
};

const pushModalState = () => {
  modalHistory.value.push({
    view: modalView.value,
    selectedAsset: selectedAsset.value,
    similarAssets: [...similarAssets.value],
  });
};

const subscribeEndpoint = async (asset) => {
  console.log("endpoints to subscribe are: ", asset);
  const res = await axios.get(`${API_BASE_URL}/users/${props.user}/endpoints`);
  const catalog = res.data[0];
  console.log("catalog uis: ", catalog);

  if (
    catalog?.endpoint == asset.endpoint &&
    catalog?.participant_did == asset.did
  ) {
    toast.add({
      severity: "error",
      summary: "Subscription Error",
      detail: "Already subscribed to endpoint",
      life: 2500,
    });
    return;
  }
  try {
    await axios.post(`${API_BASE_URL}/users/${props.user}/endpoints`, {
      endpoint: asset.endpoint,
      participant_did: asset.did,
      catalog_name: asset.catalog_name,
    });
    toast.add({
      severity: "success",
      summary: "Subscription Added",
      life: 2500,
    });
  } catch (error) {
    console.error("Error adding endpoint:", error);
    toast.add({
      severity: "error",
      summary: "Subscription Error",
      detail: `Error adding endpoint:, ${error}`,
      life: 2500,
    });
  }
};

const openAssetDetails = (asset, { preserveHistory = false } = {}) => {
  console.log("Open asset details for:", asset);
  if (showAssetModal.value && !preserveHistory) {
    pushModalState();
  }
  selectedAsset.value = asset;
  modalView.value = "details";
  showAssetModal.value = true;
};

const confirm2 = (event, asset) => {
  confirm.require({
    target: event.currentTarget,
    message: "Do you want to delete all record?",
    icon: "pi pi-info-circle",
    rejectProps: {
      label: "Cancel",
      severity: "secondary",
      outlined: true,
    },
    acceptProps: {
      label: "Delete",
      severity: "danger",
    },
    accept: async () => {
      try {
        const selectedAsset = assetsFromGlobalCatalog.value.find(
          (a) => a.id === asset.id
        );

        const dlmStoreCred = await axios.post(
          `${API_BASE_URL}/repository/find/prop/${props.user}`,
          {
            objectType: {
              name: "AcquisitionCredential",
              level: 1,
            },
            name: `AcquisitionCredential-${selectedAsset.id}`,
          },
          {
            params: {
              includeContent: "true",
              pageNumber: 1,
              pageSize: 100,
              sortBy: "_id",
            },
          }
        );

        console.log("DLM Store credential search response:", dlmStoreCred.data);

        dlmStoreCred.data.forEach(async (cred) => {
          console.log("Deleting credential with ID:", cred.id);

          await axios.delete(`${API_BASE_URL}/repository/delete/${cred.id}`, {
            params: {
              file_id: cred.id, // required query param
            },
          });
        });

        const dlmStoreOffer = await axios.post(
          `${API_BASE_URL}/repository/find/prop/${props.user}`,
          {
            objectType: {
              name: "DS2DataOfferPackage",
              level: 1,
            },
            name: selectedAsset.name,
          },
          {
            params: {
              includeContent: "true",
              pageNumber: 1,
              pageSize: 100,
              sortBy: "_id",
            },
          }
        );

        console.log("DLM Store offer search response:", dlmStoreOffer.data);

        dlmStoreOffer.data.forEach(async (offer) => {
          await axios.delete(`${API_BASE_URL}/repository/delete/${offer.id}`, {
            params: {
              file_id: offer.id, // required query param
            },
          });
        });

        togglePullCatalog();

        // console.log("Selected asset id: ", selectedAsset.key);

        // if (selectedAsset.connector) {
        //   const response = await axios.delete(
        //     `${API_BASE_URL}/provider/assets/${selectedAsset.key}`
        //   );
        //   console.log("Response from provider API:", response.data);
        //   if (response.data.success) {
        //     await axios.delete(
        //       `${API_BASE_URL}/repository/delete/${selectedAsset.store.id}`,
        //       {
        //         params: {
        //           file_id: selectedAsset.store.id, // required query param
        //         },
        //       }
        //     );
        //     emit("refresh-assets");
        //     return;
        //   }
        // } else {
        //   await axios.delete(
        //     `${API_BASE_URL}/repository/delete/${selectedAsset.store.id}`,
        //     {
        //       params: {
        //         file_id: selectedAsset.store.id, // required query param
        //       },
        //     }
        //   );
        //   emit("refresh-assets");
        //   return;
        // }

        // if (selectedOffer.length > 1) {
        //   selectedOffer.forEach(async (offer) => {
        //     assetDeleteRes = await axios.delete(
        //       `${API_BASE_URL}/provider/assets/${asset["@id"]}/${offer["@id"]}`
        //     );
        //   });
        // } else if (selectedOffer.length === 1) {
        //   assetDeleteRes = await axios.delete(
        //     `${API_BASE_URL}/provider/assets/${asset["@id"]}/${selectedOffer[0]["@id"]}`
        //   );
        // }

        // if (assetDeleteRes.data.success) {
        //   emit("refresh-assets");
        // }
      } catch (error) {
        const errormsg = error.response?.data?.detail?.[0]?.message;
        console.error("Failed to delete asset:", errormsg);
        toast.add({
          severity: "error",
          summary: "Error Message",
          detail: errormsg,
          life: 5000,
        });
      }
    },
  });
};

const confirm3 = (event, asset) => {
  confirm.require({
    target: event.currentTarget,
    message: "Do you want to delete the record?",
    icon: "pi pi-info-circle",
    rejectProps: {
      label: "Cancel",
      severity: "secondary",
      outlined: true,
    },
    acceptProps: {
      label: "Delete",
      severity: "danger",
    },
    accept: async () => {
      try {
        const selectedAsset = asset;

        console.log("Selected asset for deletion:", selectedAsset);

        // const dlmStoreCred = await axios.post(
        //   `${API_BASE_URL}/repository/find/prop/${props.user}`,
        //   {
        //     objectType: {
        //       name: "AcquisitionCredential",
        //       level: 1,
        //     },
        //     name: `AcquisitionCredential-${selectedAsset.id}`,
        //   },
        //   {
        //     params: {
        //       includeContent: "true",
        //       pageNumber: 1,
        //       pageSize: 100,
        //       sortBy: "_id",
        //     },
        //   }
        // );

        // console.log("DLM Store credential search response:", dlmStoreCred.data);

        // dlmStoreCred.data.forEach(async (cred) => {
        //   console.log("Deleting credential with ID:", cred.id);

        //   await axios.delete(`${API_BASE_URL}/repository/delete/${cred.id}`, {
        //     params: {
        //       file_id: cred.id, // required query param
        //     },
        //   });
        // });

        const dlmStoreOffer = await axios.post(
          `${API_BASE_URL}/repository/find/prop/${props.user}`,
          {
            objectType: {
              name: "DS2DataOfferPackage",
              level: 1,
            },
            name: selectedAsset.name,
          },
          {
            params: {
              includeContent: "true",
              pageNumber: 1,
              pageSize: 100,
              sortBy: "_id",
            },
          }
        );

        console.log("DLM Store offer search response:", dlmStoreOffer.data);

        const negotiatedPolicy = dlmStoreOffer.data.filter((policy) => {
          if (policy.contentData["odrl:hasPolicy"]) {
            return (
              policy?.contentData?.["odrl:hasPolicy"][0]["@id"] ===
              selectedAsset.policy[0]["@id"]
            );
          }
        });

        console.log("Negotiated policy found:", negotiatedPolicy);

        await axios.delete(
          `${API_BASE_URL}/repository/delete/${negotiatedPolicy[0].id}`,
          {
            params: {
              file_id: negotiatedPolicy[0].id, // required query param
            },
          }
        );

        togglePullCatalog();
        showNegotiatedPolicyModal.value = false;
      } catch (error) {
        const errormsg = error.response?.data?.detail?.[0]?.message;
        console.error("Failed to delete asset:", errormsg);
        toast.add({
          severity: "error",
          summary: "Error Message",
          detail: errormsg,
          life: 5000,
        });
      }
    },
  });
};

const openPolicyPreview = (policyId) => {
  console.log("Opening policy preview for policy ID:", policyId);

  if (!policyId) return;

  const repositoryUrl = `https://dlmstorebackend.idta.ds2.icelab.cloud/repository-api/get-data/${policyId}?API-KEY=C5CC3F19`;

  const encodedUrl = encodeURIComponent(repositoryUrl);

  const pcrUrl = `http://localhost:8080/policies/catView?url=${encodedUrl}&source=cat`;

  window.open(pcrUrl, "_blank");
};

const toggleAssetDetails = (asset) => {
  openAssetDetails(asset);
};

const openSimilarAssets = async (asset) => {
  console.log("Similar assets coming in: ", asset);

  const assetTags = normalizeTags(asset?.tags);

  const payload = {
    endpoint: asset.endpoint,
    did: asset.did,
    catalog_name: asset.catalog_name,
  };

  const response = await axios.post(`${API_BASE_URL}/assets`, payload);

  console.log("response is: ", response.data);
  console.log(
    "Filtered Policy: ",
    getPolicyProperty(asset.policy, "id")?.split(":")?.[1]
  );
  const globalAssetIds = new Set(
    assetsFromGlobalCatalog.value.map((a) => a.id)
  );

  similarAssets.value = response.data.filter((resAsset) => {
    // normalize tags for each asset
    const resTags = normalizeTags(resAsset?.tags);

    const tagMatch = resTags.some((tag) => assetTags.includes(tag));

    const obligationMatch =
      getComparablePolicyValue(resAsset.policy, "obligation", "leftOperand") ===
        getComparablePolicyValue(asset.policy, "obligation", "leftOperand") &&
      getComparablePolicyValue(
        resAsset.policy,
        "obligation",
        "rightOperand"
      ) ===
        getComparablePolicyValue(asset.policy, "obligation", "rightOperand");

    const sasmeAssets = resAsset.id != asset.id;

    const notInGlobalAssets = !globalAssetIds.has(resAsset.id);

    return (obligationMatch || tagMatch) && sasmeAssets && notInGlobalAssets;
  });

  console.log("List of Similar Assets: ", similarAssets.value);

  if (showAssetModal.value) {
    pushModalState();
  } else {
    showAssetModal.value = true;
  }
  modalView.value = "similar";
};

const goBackInModal = () => {
  const previousState = modalHistory.value.pop();
  if (!previousState) return;

  modalView.value = previousState.view;
  selectedAsset.value = previousState.selectedAsset;
  similarAssets.value = previousState.similarAssets;
  showAssetModal.value = true;
};

const resetModalState = () => {
  modalView.value = "details";
  modalHistory.value = [];
  similarAssets.value = [];
  selectedAsset.value = null;
};

const normalizeTags = (tags) => {
  if (!tags) return [];
  return Array.isArray(tags) ? tags : [tags];
};

const handlePortalCatalogSelect = () => {
  const selected = portalCatalogs.value.find(
    (catalog) => catalog.id === selectedPortalCatalogId.value
  );

  if (!selected) {
    newCatalogEndpoint.value = "";
    newCatalogCategory.value = "";
    return;
  }

  newCatalogEndpoint.value = selected.url || "";
  newCatalogCategory.value =
    selected.catalogType === "catalog" ? "catalog" : "marketplace";
};

const hasData = (value) => {
  if (!value) return false;

  if (Array.isArray(value)) {
    return (
      value.length > 0 && value.some((v) => Object.keys(v || {}).length > 0)
    );
  }

  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }

  return false;
};

const normalizePolicyArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const policyFieldAliases = {
  id: ["@id", "id"],
  type: ["@type", "type"],
  permission: ["odrl:permission", "permission"],
  prohibition: ["odrl:prohibition", "prohibition"],
  obligation: ["odrl:obligation", "obligation"],
  hasPolicy: ["odrl:hasPolicy", "hasPolicy", "policy"],
  constraint: ["odrl:constraint", "constraint"],
  duty: ["odrl:duty", "duty"],
  leftOperand: [
    "odrl:leftOperand",
    "leftOperand",
    "leftoperand",
    "left_operand",
  ],
  operator: ["odrl:operator", "operator"],
  rightOperand: [
    "odrl:rightOperand",
    "rightOperand",
    "rightoperand",
    "right_operand",
  ],
};

const getPolicyProperty = (source, aliasKey) => {
  if (!source || typeof source !== "object") return undefined;

  const aliases = policyFieldAliases[aliasKey] || [aliasKey];
  for (const alias of aliases) {
    if (source[alias] !== undefined && source[alias] !== null) {
      return source[alias];
    }
  }

  return undefined;
};

const getPolicySectionData = (policy, sectionKey) => {
  return getPolicyProperty(policy, sectionKey);
};

const getPolicyFieldValue = (source, fieldKey, fallbackSource) => {
  const value = getPolicyProperty(source, fieldKey);
  if (value !== undefined) return value;
  return getPolicyProperty(fallbackSource, fieldKey);
};

const formatPolicyValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "object") {
    return (
      getPolicyProperty(value, "id") ||
      value["@value"] ||
      value.value ||
      JSON.stringify(value)
    );
  }
  return value;
};

const getPolicyDisplayName = (index) => {
  return `Policy ${index + 1}`;
};

const getPolicyKey = (policy, index) => {
  return getPolicyProperty(policy, "id") || `policy-${index}`;
};

const getPolicyConstraints = (rule) => {
  const constraints = normalizePolicyArray(
    getPolicyProperty(rule, "constraint")
  );
  return constraints.length ? constraints : [rule];
};

const getPolicyDuty = (rule) => {
  const duty = normalizePolicyArray(getPolicyProperty(rule, "duty"));
  console.log("getPolicyDuty: ", duty);
  console.log("duty length: ", duty.length);

  return duty.length > 0 ? duty : [];
};

const policyHasRules = (policy) => {
  return policyRuleSections.some((section) =>
    hasData(getPolicySectionData(policy, section.key))
  );
};

const getPolicyRuleCount = (policy) => {
  return policyRuleSections.reduce((count, section) => {
    return (
      count +
      normalizePolicyArray(getPolicySectionData(policy, section.key)).length
    );
  }, 0);
};

const getComparablePolicyValue = (policy, sectionKey, fieldKey) => {
  const section = normalizePolicyArray(
    getPolicySectionData(policy, sectionKey)
  )[0];
  const constraint = getPolicyConstraints(section)[0];
  return formatPolicyValue(getPolicyFieldValue(constraint, fieldKey, section));
};

const negotiatePolicy = () => {};

const normalizeNegotiatedItem = (item) => {
  return {
    id: item["@id"] || item.id,
    name: item.name,
    description: item.description,
    policy: item["odrl:hasPolicy"] || item.policy,
    endpoint: item.EndPoint || item.endpoint,
    did: item.participantId,
    catalog_name: item.CatalogName,
    CreatorInfo: item.creatorName,
    pricingPlan: item.pricingPlans,
    format: item.format,
    AssetVersion: item.assetVersion,
    CreationDate: item.created || item["dct:created"],
    tags: item.tags || [],
    keywords: item.keywords || [],
    theme: item.theme || [],
    Offer: item,
  };
};

const openNegotiatedPolicyModal = (asset) => {
  negotiatedPolicyAssets.value = (asset.negotiatedPolicy || []).map(
    normalizeNegotiatedItem
  );
  negotiatedPolicyAssets.value.unshift(
    (({ negotiatedPolicy, ...rest }) => rest)(asset)
  );

  console.log("Negotiated policy assets: ", negotiatedPolicyAssets.value);

  showNegotiatedPolicyModal.value = true;
};

const viewPolicy = async (policy, asset) => {
  console.log("Viewing policy:", policy);
  console.log("Viewing asset:", asset);
  const policyId = getPolicyProperty(policy, "id");

  try {
    console.log(
      "policy id: ",
      `${API_BASE_URL}/dataOfferPolicy/${policy["@id"] || policy.id}`
    );

    const response = await axios.post(`${API_BASE_URL}/dataOfferPolicy`, {
      url: `${API_BASE_URL}/dataOfferPolicy/${policy["@id"] || policy.id}`,
    });
    window.open(response.data.url, "_blank");
  } catch (error) {
    console.log("Policy ID could not be decoded:", policyId, error);
  }

  // selectedPolicyForPreview.value = policy;
  // showPolicyDialog.value = true;
};

const getPolicyConstraintValue = (asset, policyKey, fieldKey) => {
  const sectionKey = policyKey.replace("odrl:", "");
  const normalizedFieldKey = fieldKey.replace("odrl:", "");
  return formatPolicyValue(
    getComparablePolicyValue(asset?.policy, sectionKey, normalizedFieldKey)
  );
};

const operatorHumanLabels = {
  "odrl:gteq": "≥",
  gteq: "≥",
  "odrl:lteq": "≤",
  lteq: "≤",
  "odrl:eq": "=",
  eq: "=",
  "odrl:gt": ">",
  gt: ">",
  "odrl:lt": "<",
  lt: "<",
  "odrl:neq": "≠",
  neq: "≠",
  "odrl:isPartOf": "is part of",
  isPartOf: "is part of",
  "odrl:isAnyOf": "is any of",
  isAnyOf: "is any of",
  "odrl:isAllOf": "is all of",
  isAllOf: "is all of",
  "odrl:isNoneOf": "is none of",
  isNoneOf: "is none of",
};

const leftOperandHumanLabels = {
  "odrl:dateTime": "Date/Time",
  dateTime: "Date/Time",
  "odrl:spatial": "Location",
  spatial: "Location",
  "odrl:purpose": "Purpose",
  purpose: "Purpose",
  "odrl:count": "Usage Count",
  count: "Usage Count",
  "odrl:absoluteSize": "Size",
  absoluteSize: "Size",
  "odrl:payAmount": "Payment",
  payAmount: "Payment",
  "odrl:language": "Language",
  language: "Language",
  "odrl:industry": "Industry",
  industry: "Industry",
  "odrl:resolution": "Resolution",
  resolution: "Resolution",
  "odrl:percentage": "Percentage",
  percentage: "Percentage",
  "odrl:systemDevice": "Device",
  systemDevice: "Device",
  "odrl:media": "Media Type",
  media: "Media Type",
  "odrl:deliveryChannel": "Delivery Channel",
  deliveryChannel: "Delivery Channel",
  "odrl:recipient": "Recipient",
  recipient: "Recipient",
  "odrl:event": "Event",
  event: "Event",
};

const formatConstraintRightValue = (value) => {
  if (value === null || value === undefined) return "N/A";
  let raw = value;
  if (typeof raw === "object") {
    raw = raw["@value"] ?? raw.value ?? JSON.stringify(raw);
  }
  raw = String(raw);
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    try {
      return new Date(raw).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {}
  }
  return raw.replace(/^odrl:/, "");
};

const humanizeConstraintText = (constraint, rule) => {
  const leftRaw = formatPolicyValue(
    getPolicyFieldValue(constraint, "leftOperand", rule)
  );
  const opRaw = formatPolicyValue(
    getPolicyFieldValue(constraint, "operator", rule)
  );
  const rightRaw =
    getPolicyFieldValue(constraint, "rightOperand", rule) ??
    getPolicyFieldValue(rule, "rightOperand", null);

  const leftLabel =
    leftOperandHumanLabels[leftRaw] ??
    (leftRaw && leftRaw !== "N/A" ? leftRaw.replace(/^odrl:/, "") : leftRaw);
  const opLabel =
    operatorHumanLabels[opRaw] ??
    (opRaw && opRaw !== "N/A" ? opRaw.replace(/^odrl:/, "") : opRaw);
  const rightLabel = formatConstraintRightValue(rightRaw);

  return `${leftLabel} ${opLabel} ${rightLabel}`;
};

const getAssetCardPolicySections = (asset) => {
  const policies = normalizePolicyArray(asset?.policy).filter(Boolean);

  const sections = [];

  for (const policy of policies) {
    for (const section of policyRuleSections) {
      const sectionData = getPolicySectionData(policy, section.key);

      if (!hasData(sectionData)) continue;

      const rules = normalizePolicyArray(sectionData);

      const constraints = [];
      const duties = [];
      for (const rule of rules) {
        for (const c of getPolicyConstraints(rule)) {
          constraints.push(humanizeConstraintText(c, rule));
        }
        for (const d of getPolicyDuty(rule)) {
          duties.push(d);
        }
      }

      if (constraints.length) {
        sections.push({
          type: section.key,
          label: section.label,
          className: section.className,
          constraints,
          duties,
        });
      }
    }
  }

  console.log("Asset card policy sections:", sections);

  return sections;
};

const hasAssetPolicySummary = (asset) =>
  getAssetCardPolicySections(asset).length > 0;

watch(selectedPortalCatalogId, () => {
  handlePortalCatalogSelect();
});

watch(showAssetModal, (isVisible) => {
  if (!isVisible) {
    resetModalState();
  }
});

const addCatalog = async () => {
  if (!newCatalogEndpoint.value.trim() || !newCatalogCategory.value) {
    return;
  }

  if (!isValidUrl(newCatalogEndpoint.value)) {
    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: "Please enter a valid URL starting with http:// or https://",
      life: 3000,
    });
    return;
  }

  if (
    catalogs.value.some(
      (entry) =>
        entry.endpoint === newCatalogEndpoint.value &&
        entry.category === newCatalogCategory.value
    )
  ) {
    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: "This catalog is already added.",
      life: 3000,
    });
    return;
  }

  try {
    const response = await axios.post(
      `${API_BASE_URL}/users/${props.user}/catalogs`,
      {
        endpoint: newCatalogEndpoint.value,
        category: newCatalogCategory.value,
      }
    );

    await fetchCatalogs(props.user);
    toast.add({
      severity: "success",
      summary: "Catalog Added",
      life: 3000,
    });
    emit("refresh-catalogs");

    const newCatalog = response?.data?.catalogs?.find(
      (entry) =>
        entry.endpoint === newCatalogEndpoint.value &&
        entry.category === newCatalogCategory.value
    );

    openMetadataWizard(
      newCatalog || {
        name: newCatalogEndpoint.value,
        endpoint: newCatalogEndpoint.value,
        category: newCatalogCategory.value,
      }
    );

    //newCatalogName.value = "";
    newCatalogEndpoint.value = "";
    newCatalogCategory.value = "";
    selectedPortalCatalogId.value = "";
    //newCatalogProtocol.value = "";
    //newCatalogDescription.value = "";
  } catch (error) {
    console.error("Error adding catalog:", error);
    toast.add({
      severity: "error",
      summary: "Error Message",
      detail: error.response.data.detail,
      life: 3000,
    });
  }
};

const fetchCatalogs = async (user) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/users/${user}/catalogs`);
    console.log("API response for catalogs:", res);

    catalogs.value = res.data;
    console.log("Fetched catalogs:", catalogs.value);
  } catch (error) {
    console.error("Error fetching catalogs:", error);
  }
};

const getCatalogSchema = async (catalog) => {
  // Placeholder for upcoming catalog schema API integration.
  // Expected API response shape:
  // { fields: [{ name: "carbonFootprintScore", required: true }, ...] }
  return {
    fields: [
      { name: "carbonFootprintScore", required: true },
      { name: "OrganisationName", required: true },
      { name: "isPayable", required: true },
      { name: "colour", required: false },
    ],
  };
};

const openMetadataWizard = async (catalog) => {
  try {
    console.log("Loading metadata schema for catalog:", catalog);
    selectedCatalog.value = catalog;
    const schema = await getCatalogSchema(catalog);
    schemaFields.value = Array.isArray(schema?.fields) ? schema.fields : [];
    fieldDefaults.value = {};
    fieldInputs.value = schemaFields.value.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, {});

    console.log("field inputs initialized to:", fieldInputs.value);

    const response = await axios.post(
      `${API_BASE_URL}/repository/find/prop/${props.user}`,
      {
        objectType: {
          name: "globalCatalog",
        },
        name: catalog.name,
      },
      {
        params: {
          includeContent: "true",
          pageNumber: 1,
          pageSize: 100,
          sortBy: "_id",
        },
      }
    );

    console.log(
      "Repository search response for existing metadata:",
      response.data
    );

    if (response.data.length > 0) {
      fieldInputs.value = response.data[0].contentData.defaultValues || {};
      defaultValueExist.value = true;
      objectId.value = response.data[0].id;
    }

    console.log("field inputs initialized after:", fieldInputs.value);

    if (!schemaFields.value.length) {
      toast.add({
        severity: "warn",
        summary: "No Schema Fields",
        detail: "Schema API returned no fields for this catalog.",
        life: 3000,
      });
      return;
    }
    showFieldPopup.value = true;
  } catch (error) {
    console.error("Error loading metadata schema:", error);
    toast.add({
      severity: "error",
      summary: "Schema Error",
      detail: "Unable to load metadata schema for this catalog.",
      life: 3000,
    });
  }
};

const closeMetadataWizard = () => {
  showFieldPopup.value = false;
  fieldInputs.value = {};
};

const toggleSelectedAssetDetails = (asset) => {
  selectedAssetDetails.value = asset;
  showSelectedAssetDetails.value = true;
};

const castFieldValue = (value, type) => {
  if (type === "boolean") return value === "true";
  if (type === "number") return Number(value);
  return value;
};

const acquireAsset = async (asset) => {
  console.log("user is: ", props.user);

  console.log("asset to acquire is: ", asset);

  const price = asset.Offer?.pricingPlans?.price;

  console.log("Price for the asset is: ", price);

  if (price > 0) {
    toast.add({
      severity: "error",
      summary: "Paid Asset",
      detail: "This paid asset can only be acquired from the data marketplace",
      life: 5000,
    });
    return;
  }

  const signer = await axios.post(
    "https://idm.ds2.icelab.cloud/idm-api/credentials/sign",
    {
      organizationDid: props.user,
      credentialType: "AcquisitionCredential",
      expirationDate: "2027-04-15T03:21:42.207",
      credentialSubject: {
        id: asset.Offer.participantId,
        claims: {
          offerId: getPolicyProperty(
            getPolicyProperty(asset.Offer, "hasPolicy"),
            "id"
          ),
          asset: asset.Offer.id,
          paid: false,
        },
      },
    },
    {
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
    }
  );

  await axios.post(
    `${API_BASE_URL}/repository/insert`,
    {
      ownerId: props.user,
      externalId: asset.Offer.participantId,
      objectType: {
        name: "AcquisitionCredential",
        level: 1,
      },
      name: `AcquisitionCredential-${asset.Offer.id}`,
      description: "",
      comments: "string",
      version: "string",
      tags: ["string"],
      metadata: {
        additionalProp1: {},
      },
      contentType: "json",
      contentData: signer.data,
    },
    {
      params: {
        "API-KEY": "C5CC3F19",
      },
      headers: {
        Accept: "application/json",
        "API-KEY": "C5CC3F19",
      },
    }
  );

  const dlmStoreCred = await axios.post(
    `${API_BASE_URL}/repository/find/prop/${props.user}`,
    {
      objectType: {
        name: "AcquisitionCredential",
        level: 1,
      },
      name: `AcquisitionCredential-${asset.Offer.id}`,
    },
    {
      params: {
        includeContent: "true",
        pageNumber: 1,
        pageSize: 100,
        sortBy: "_id",
      },
    }
  );

  console.log("DLM Store credential search response:", dlmStoreCred.data);

  if (!dlmStoreCred.data.length > 0) {
    toast.add({
      severity: "info",
      summary: "Acquisition Credential Failed",
      detail: "Failed to Acquire Credential. Please try again.",
      life: 3000,
    });
    return;
  }

  asset["Credential"] = dlmStoreCred.data[0].id;

  const dlmStore = await axios.post(
    `${API_BASE_URL}/repository/insert`,
    {
      ownerId: props.user,
      externalId: asset.name,
      objectType: {
        name: "DS2DataOfferPackage",
        level: 1,
      },
      name: asset.name,
      description: asset.description,
      comments: "string",
      version: "string",
      tags: ["string"],
      metadata: {
        source: "LocalCatalog",
      },
      contentType: "json",
      contentData: asset,
    },
    {
      params: {
        "API-KEY": "C5CC3F19",
      },
      headers: {
        Accept: "application/json",
        "API-KEY": "C5CC3F19",
      },
    }
  );

  console.log("DLM Store response:", dlmStore.data);

  if (dlmStore.data.success) {
    toast.add({
      severity: "success",
      summary: "Data Offer Acquired",
      life: 3000,
    });
    showAssetModal.value = false;
    await togglePullCatalog();
  }
};

const saveFieldDefaults = async () => {
  const defaults = {};

  schemaFields.value.forEach((field) => {
    const rawValue = (fieldInputs.value[field.name] ?? "").toString().trim();
    if (!rawValue) return;
    defaults[field.name] = castFieldValue(rawValue, inferFieldType(field));
  });

  fieldDefaults.value = defaults;

  const payload = buildCatalogMetadataPayload();
  console.log("Generated metadata payload:", payload);

  const response = await axios.post(
    `${API_BASE_URL}/repository/insert`,
    payload
  );

  console.log("Payload upload response:", response.data);

  closeMetadataWizard();
  toast.add({
    severity: "success",
    summary: "Payload Ready",
    detail: "Metadata payload generated. Copy and upload it to your database.",
    life: 3000,
  });
};

const editFieldDefaults = async () => {
  const defaults = {};

  schemaFields.value.forEach((field) => {
    const rawValue = (fieldInputs.value[field.name] ?? "").toString().trim();
    if (!rawValue) return;
    defaults[field.name] = castFieldValue(rawValue, inferFieldType(field));
  });

  fieldDefaults.value = defaults;

  const payload = buildCatalogMetadataPayload();
  console.log("Generated metadata payload:", payload);

  const response = await axios.post(
    `${API_BASE_URL}/repository/update/full`,
    payload
  );

  console.log("Payload upload response:", response.data);

  closeMetadataWizard();
  toast.add({
    severity: "success",
    summary: "Payload Ready",
    detail: "Metadata payload generated. Copy and upload it to your database.",
    life: 3000,
  });
};

const buildCatalogMetadataPayload = () => {
  return {
    ownerId: props.user,
    externalId: selectedCatalog.value?.id,
    objectType: {
      name: "globalCatalog",
      level: 0,
    },
    name: selectedCatalog.value?.name || selectedCatalog.value?.endpoint,
    description: selectedCatalog.value?.description,
    comments: "",
    version: "",
    tags: [],
    metadata: {},
    contentType: "json",
    contentData: {
      fields: schemaFields.value,
      defaultValues: fieldDefaults.value,
      generatedAt: new Date().toISOString(),
    },
    ...(defaultValueExist.value && { id: objectId.value }), // 👈 here
  };
};

const copyPayload = async (payload) => {
  try {
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.add({
      severity: "success",
      summary: "Copied",
      detail: "Payload copied to clipboard.",
      life: 2000,
    });
  } catch (error) {
    console.error("Failed to copy payload:", error);
    toast.add({
      severity: "error",
      summary: "Copy Failed",
      detail: "Unable to copy payload to clipboard.",
      life: 2000,
    });
  }
};

const togglePullCatalog = async () => {
  assetsFromGlobalCatalog.value = [];
  try {
    const response = await axios.post(
      `${API_BASE_URL}/repository/find/prop/${props.user}`,
      {
        objectType: {
          name: "DS2DataOfferPackage",
        },
      },
      {
        params: {
          includeContent: "true",
          pageNumber: 1,
          pageSize: 100,
          sortBy: "_id",
        },
      }
    );
    console.log("Repository search response for policies:", response.data);

    const catalogAssets = response.data;

    console.log(
      "Assets for catalog from repository search:",
      catalogAssets[0].contentData.Offer
    );

    const payload = {
      catalog: catalogAssets.map((asset) => {
        return asset.contentData?.Offer || asset.contentData;
      }),
    };

    const assetRes = await axios.post(`${API_BASE_URL}/assets`, payload);
    assetsFromGlobalCatalog.value = assetRes.data || [];

    console.log(
      "Assets for catalog after toggle:",
      assetsFromGlobalCatalog.value
    );
  } catch (error) {
    console.error("Error toggling pull panel:", error);
  }
};

const removeCatalog = async (event, index) => {
  const catalog = catalogs.value[index];

  confirm.require({
    target: event.currentTarget,
    message: "Do you want to delete this record?",
    icon: "pi pi-info-circle",
    rejectProps: {
      label: "Cancel",
      severity: "secondary",
      outlined: true,
    },
    acceptProps: {
      label: "Delete",
      severity: "danger",
    },
    accept: async () => {
      console.log("Delete clicked");
      const response = await axios.post(
        `${API_BASE_URL}/repository/find/prop/${user}`,
        { objectType: { name: "User" } },
        {
          params: {
            includeContent: "true",
            pageNumber: 1,
            pageSize: 200,
            sortBy: "_id",
          },
        }
      );
      console.log(response.data);
      const userRecord = response.data.find((record) => record.name === user);
      console.log("userRecord:", userRecord);
      console.log("catalog to remove:", catalog);
      console.log(
        "userRecord content catalogs before:",
        userRecord.contentData?.catalogs
      );
      userRecord.contentData.catalogs = userRecord.contentData.catalogs.filter(
        (entry) => {
          return !(
            entry.endpoint === catalog.endpoint &&
            entry.category === catalog.category
          );
        }
      );

      await axios.post(`${API_BASE_URL}/repository/update/full`, userRecord);

      const catalogResponse = await axios.post(
        `${API_BASE_URL}/repository/find/prop/${user}`,
        { objectType: { name: "globalCatalog" } },
        {
          params: {
            includeContent: "true",
            pageNumber: 1,
            pageSize: 200,
            sortBy: "_id",
          },
        }
      );

      console.log("Catalog records response:", catalogResponse.data);
      console.log("Catalog to match:", catalog);

      const globalCatalogRecord = catalogResponse.data.find(
        (record) => record.name === catalog.name
      );
      console.log("Global Catalog Records:", globalCatalogRecord);

      const catalogRes = await axios.delete(
        `${API_BASE_URL}/repository/delete/${globalCatalogRecord.id}`,
        {
          params: {
            file_id: globalCatalogRecord.id,
          },
        }
      );

      if (catalogRes.data.acknowledged) {
        await fetchCatalogs(props.user);
        toast.add({
          severity: "warn",
          summary: "Catalog removed",
          life: 3000,
        });
      }

      // await axios.post(`${API_BASE_URL}/users/${props.user}/catalogs/remove`, {
      //   name: catalog.name,
      //   endpoint: catalog.endpoint,
      //   category: catalog.category,
      // });
      // await fetchCatalogs(props.user);
      // emit("refresh-catalogs");
    },
  });
};

const catalogKey = (catalog, index) => {
  return `${catalog.endpoint}-${catalog.category}-${index}`;
};

onMounted(async () => {
  await fetchCatalogs(props.user);
  await fetchPortalCatalogs();
  await togglePullCatalog();
});
</script>

<style scoped>
.subscriber-view {
  --brand-primary: #0f766e;
  --brand-primary-strong: #115e59;
  --brand-soft: #f0fdfa;
  --slate-100: #e2e8f0;
  --slate-200: #cbd5e0;
  --slate-500: #64748b;
  --modal-backdrop: rgba(15, 23, 42, 0.58);
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  font-family: "Manrope", "Segoe UI", sans-serif;
  color: #2d3748;
}

.header-section {
  background: linear-gradient(135deg, var(--brand-primary) 0%, #155e75 100%);
  border-radius: 16px;
  padding: 2.5rem;
  margin-bottom: 3rem;
  color: white;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 2rem;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.title {
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.catalog-badge {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.subscription-section {
  margin-bottom: 3rem;
}

.subscription-card {
  display: grid;
  grid-template-columns: repeat(2, minmax(300px, 1fr));
  gap: 16px;
}

.card-header {
  margin-bottom: 2rem;
}

.section-description {
  font-size: 1rem;
  color: black;
  margin: 0;
  line-height: 1.6;
}

.input-section {
  margin-bottom: 2.5rem;
}

.input-group {
  max-width: 900px;
}

.input-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.75rem;
}

.catalog-row {
  grid-template-columns: 1fr 1fr 220px;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: #f7fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.5rem;
  transition: all 0.3s ease;
}

.input-container:focus-within {
  border-color: #009688;
  background: white;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.select-container {
  padding: 0.5rem 0.75rem;
}

.input-icon {
  width: 20px;
  height: 20px;
  color: #a0aec0;
  margin: 0 0.75rem;
  flex-shrink: 0;
}

.endpoint-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.75rem 0;
  font-size: 1rem;
  color: #2d3748;
  outline: none;
}

.endpoint-input::placeholder {
  color: #a0aec0;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--brand-primary);
  color: black;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  height: fit-content;
}

.add-btn:hover:not(:disabled) {
  background: var(--brand-primary-strong);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.add-btn:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.input-hint {
  font-size: 0.85rem;
  color: #718096;
  margin: 0;
}

.subscriptions-section {
  border-top: 1px solid #e2e8f0;
  padding-top: 2rem;
}

.payload-section {
  margin-top: 2rem;
  border-top: 1px solid var(--slate-100);
  padding-top: 2rem;
}

.payload-list {
  display: grid;
  gap: 1rem;
}

.payload-card {
  border: 1px solid var(--slate-100);
  border-radius: 14px;
  background: #ffffff;
  overflow: hidden;
}

.payload-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1rem 0.75rem 1rem;
}

.payload-title {
  margin: 0;
  font-size: 1rem;
}

.payload-subtitle {
  margin: 0.2rem 0 0;
  color: var(--slate-500);
  font-size: 0.82rem;
  word-break: break-all;
}

.payload-json {
  margin: 0;
  padding: 1rem;
  background: #0f172a;
  color: #cbd5e1;
  font-size: 0.76rem;
  max-height: 280px;
  overflow: auto;
}

.copy-btn,
.configure-btn {
  border: 1px solid var(--slate-200);
  border-radius: 8px;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.78rem;
}

.copy-btn {
  padding: 0.45rem 0.65rem;
}

.configure-btn {
  padding: 0.45rem 0.7rem;
  margin-left: auto;
}

.copy-btn:hover,
.configure-btn:hover {
  border-color: var(--brand-primary);
  color: var(--brand-primary-strong);
}

.pull-toggle {
  border: 1px solid var(--slate-200);
  border-radius: 8px;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.78rem;
  padding: 0.45rem 0.7rem;
  margin-left: 0.5rem;
}

.pull-toggle:hover {
  border-color: var(--brand-primary);
  color: var(--brand-primary-strong);
}

.subscriptions-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 1.5rem 0;
}

.subscriptions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.catalog-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.subscription-item {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  transition: all 0.2s ease;
}

.subscription-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.subscription-icon {
  width: 20px;
  height: 20px;
  color: #48bb78;
  flex-shrink: 0;
}

.subscription-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.endpoint-url {
  font-weight: 500;
  color: #2d3748;
  font-size: 0.95rem;
}

.participant-did {
  font-size: 0.85rem;
  color: #718096;
  font-family: monospace;
}

.subscription-status {
  font-size: 0.8rem;
  color: var(--brand-primary);
  font-weight: 600;
  margin-top: 0.25rem;
}

.subscription-meta {
  font-size: 0.85rem;
  color: #718096;
}

.unsubscribe-btn {
  background: #fed7d7;
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  color: #e53e3e;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 1rem;
}

.unsubscribe-btn:hover {
  background: #feb2b2;
  transform: scale(1.05);
}

.unsubscribe-icon {
  width: 16px;
  height: 16px;
}

.empty-subscriptions {
  text-align: center;
  padding: 3rem 2rem;
  border-top: 1px solid #e2e8f0;
}

.empty-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  background: #f7fafc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
}

.empty-icon svg {
  width: 32px;
  height: 32px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.empty-description {
  color: #718096;
  margin: 0;
  font-size: 0.95rem;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--modal-backdrop);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-card {
  width: min(620px, 100%);
  border-radius: 18px;
  border: 1px solid #dbeafe;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 30px 70px rgba(2, 8, 23, 0.35);
}

.modal-header {
  padding: 1.35rem 1.35rem 0.6rem;
}

.modal-header h2 {
  margin: 0.15rem 0;
  font-size: 1.4rem;
}

.modal-subtitle {
  margin: 0.2rem 0 0;
  color: #4a5568;
  font-size: 0.9rem;
}

.modal-step {
  margin: 0;
  color: var(--brand-primary-strong);
  font-weight: 800;
  font-size: 0.82rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.modal-context {
  margin: 0;
  color: var(--slate-500);
  font-size: 0.9rem;
}

.modal-content {
  padding: 1rem 1.35rem;
}

.fields-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.field-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.field-card .input-container {
  margin: 0;
}

.field-tag-row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.8rem;
}

.field-name {
  font-weight: 800;
  font-size: 1rem;
  color: #0f172a;
}

.empty-category {
  text-align: center;
  padding: 2.5rem 1.5rem 2rem;
  color: #64748b;
}

.empty-category .empty-icon {
  font-size: 2.25rem;
  margin-bottom: 0.5rem;
}

.empty-category h4 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  color: #0f172a;
}

.empty-category p {
  margin: 0;
  font-size: 0.9rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
}

.field-required {
  background: #dcfce7;
  color: #166534;
  border-radius: 999px;
  font-size: 0.74rem;
  padding: 0.22rem 0.55rem;
  font-weight: 700;
}

.field-required.optional {
  background: #e2e8f0;
  color: #334155;
}

.modal-input {
  background: #fff;
  margin-bottom: 0.5rem;
}

.modal-hint {
  margin: 0.45rem 0 0;
  color: var(--slate-500);
  font-size: 0.82rem;
}

.modal-actions {
  padding: 0 1.35rem 1.3rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
}

.ghost-btn {
  border-radius: 8px;
  border: 1px solid var(--slate-200);
  background: #fff;
  color: #334155;
  padding: 0.75rem 1rem;
  font-weight: 700;
  cursor: pointer;
}

.ghost-btn:hover {
  border-color: var(--slate-500);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .catalog-row {
    grid-template-columns: 1fr;
  }

  .input-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .add-btn {
    justify-content: center;
    margin-top: 0.5rem;
  }
}

@media (max-width: 768px) {
  .subscriber-view {
    padding: 1rem;
  }

  .title {
    font-size: 2rem;
  }

  .subscription-card {
    padding: 1.5rem;
  }

  .subscription-item {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .subscription-content {
    gap: 0.75rem;
  }

  .detail-grid,
  .permissions-grid {
    grid-template-columns: 1fr;
  }

  .unsubscribe-btn {
    margin-left: 0;
    align-self: flex-end;
  }
}

@media (max-width: 480px) {
  .title {
    font-size: 1.75rem;
  }

  .subscription-card {
    padding: 1.25rem;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.subscription-item {
  animation: fadeIn 0.3s ease forwards;
}

/* Pull Panel */
.pull-panel {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  padding: 1.25rem;
}

.pull-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
}

.pull-panel-header h4 {
  margin: 0 0 0.3rem;
  font-size: 1.05rem;
  color: #0f172a;
}

.pull-panel-header p {
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
}

.pull-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.empty-pull {
  padding: 1rem;
  border-radius: 12px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.9rem;
}

.pull-grid {
  display: grid;
  gap: 1.2rem;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
}

.catalog-pill {
  background: #e2e8f0;
  color: #334155;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.pull-asset-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pull-asset-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
}

.pull-checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.pull-checkbox input {
  opacity: 0;
  position: absolute;
}

.checkbox-mark {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid #94a3b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: white;
}

.pull-checkbox input:checked + .checkbox-mark {
  border-color: var(--brand-primary);
  background: var(--brand-primary);
  box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.18);
}

.pull-checkbox input:checked + .checkbox-mark::after {
  content: "";
  width: 8px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-top: -2px;
}

.pull-asset-body {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.pull-asset-body h4 {
  margin: 0;
  font-size: 1.05rem;
  color: #0f172a;
}

.pull-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.35rem;
}

.pull-tag {
  background: #e2e8f0;
  color: #475569;
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.pull-asset-body p {
  margin: 0 0 0.7rem;
  color: #475569;
  font-size: 0.9rem;
}

.pull-meta {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.78rem;
  color: #64748b;
  margin-bottom: 0.6rem;
  word-break: break-word;
}

.pull-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.chip {
  background: #0f766e;
  color: white;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.chip.ghost {
  background: #e0f2fe;
  color: #0369a1;
}

.selected-assets {
  margin-top: 2rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 1.5rem;
}

.selected-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.selected-header h4 {
  margin: 0;
  font-size: 1.1rem;
  color: #0f172a;
}

.selected-header span {
  font-size: 0.85rem;
  color: #64748b;
}

.selected-list {
  display: grid;
  gap: 0.75rem;
}

.selected-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.9rem 1rem;
}

.selected-item p {
  margin: 0.25rem 0 0;
  font-size: 0.78rem;
  color: #64748b;
  word-break: break-word;
}

@media (max-width: 900px) {
  .pull-toggle {
    margin-left: 0;
  }
}

.modal__content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-toolbar {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0.75rem;
}

.asset-details {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.modal-footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.detail-accordion {
  border: 1px solid #dbe4f0;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
}

.detail-accordion summary {
  list-style: none;
  cursor: pointer;
  padding: 1rem 1.15rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.detail-accordion summary::-webkit-details-marker {
  display: none;
}

.detail-accordion summary::after {
  content: "+";
  color: var(--brand-primary-strong);
  font-weight: 900;
  font-size: 1.05rem;
}

.detail-accordion[open] summary::after {
  content: "-";
}

.detail-panel {
  padding: 1rem 1.15rem 1.15rem;
  border-top: 1px solid #e2e8f0;
}

.detail-section h3 {
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}
.section-header {
  background-color: white;
  padding: 1.5rem;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: row;
  gap: 4px;
}

.policy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 16px;
}

.policy-modern-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
  padding: 18px;
}

.policy-modern-card__header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
}

.policy-modern-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: #00796b;
  background: #e6f6f3;
}

.policy-modern-card__title {
  min-width: 0;
}

.policy-modern-card__eyebrow {
  display: block;
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.policy-modern-card__title h4 {
  margin: 2px 0 0;
  color: #0f172a;
  font-size: 0.98rem;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.policy-modern-card__count {
  border: 1px solid #cde8e3;
  border-radius: 999px;
  background: #f0fbf8;
  color: #00796b;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 5px 10px;
  white-space: nowrap;
}

.policy-status-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px 12px;
}

.policy-status-time {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: #475569;
}

.policy-status-time > i {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border-radius: 8px;
  background: #eef2ff;
  color: #4f46e5;
}

.policy-status-time span {
  display: block;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
}

.policy-status-time strong {
  display: block;
  margin-top: 2px;
  color: #1e293b;
  font-size: 0.86rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.policy-negotiated-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #b7e4d7;
  border-radius: 999px;
  background: #ecfdf5;
  color: #047857;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 6px 10px;
  white-space: nowrap;
}

.policy-constraint-row span,
.policy-preview-dialog__header span {
  display: block;
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.policy-constraint-row strong {
  display: block;
  margin-top: 4px;
  color: #1e293b;
  font-size: 0.88rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.policy-rule-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.policy-rule-section {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fbfdff;
  padding: 12px;
}

.policy-rule-section__heading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 800;
}

.policy-rule-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #009688;
}

.policy-rule-dot--prohibition {
  background: #dc2626;
}

.policy-rule-dot--obligation {
  background: #f59e0b;
}

.policy-rule,
.policy-constraint {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.policy-constraint + .policy-constraint {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #cbd5e1;
}

.policy-constraint-row {
  display: grid;
  grid-template-columns: minmax(90px, 0.42fr) minmax(0, 1fr);
  gap: 10px;
  align-items: start;
}

.policy-constraint-row strong {
  margin-top: 0;
  font-weight: 600;
}

.policy-empty-state {
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.9rem;
  padding: 14px;
  text-align: center;
}

.policy-modern-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: auto;
  padding-top: 2px;
}

.policy-action {
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.86rem;
  font-weight: 800;
  padding: 9px 14px;
  transition: all 0.2s ease;
}

.policy-action--ghost {
  background: #ffffff;
  border-color: #cbd5e1;
  color: #334155;
}

.policy-action--ghost:hover {
  background: #f8fafc;
}

.policy-action--primary {
  background: #009688;
  color: #ffffff;
}

.policy-action--primary:hover {
  background: #00796b;
}

.policy-preview-dialog {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.policy-preview-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 16px;
}

.policy-preview-dialog__header h3 {
  margin: 3px 0 0;
  color: #0f172a;
  font-size: 1rem;
  overflow-wrap: anywhere;
}

.policy-preview-dialog pre {
  max-height: 56vh;
  overflow: auto;
  margin: 0;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #0f172a;
  color: #e2e8f0;
  font-size: 0.82rem;
  line-height: 1.55;
  padding: 16px;
  white-space: pre-wrap;
  word-break: break-word;
}

.permissions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 15px;
}

.permission-card {
  border: 1px solid #ddd;
  padding: 12px;
  border-radius: 8px;
  background: #fff;
}

.details-policy-header {
  text-align: center;
  background-color: #f5f7fb;
  font-weight: 600;
  margin-bottom: 10px;
}

.row {
  display: flex;
  justify-content: space-between;
  margin: 4px 0;
}

.detail-item label {
  font-weight: 600;
  color: #4a5568;
  font-size: 0.9rem;
}

.detail-item span {
  color: #2d3748;
  line-height: 1.5;
}
.btn--secondary {
  background: #f7fafc;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.btn--secondary:hover {
  background: #edf2f7;
}
.btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-size: 0.9rem;
}
.btn--primary {
  background: #009688;
  color: white;
}

/* Policy card summary */
.policy-card-summary {
  margin-bottom: 0.5rem;
}

.policy-card-section {
  margin-bottom: 0.55rem;
}

.policy-card-section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 0.3rem;
  letter-spacing: 0.03em;
}

.policy-card-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.policy-card-pill {
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
  font-size: 0.76rem;
  font-weight: 600;
  white-space: nowrap;
}

.policy-card-pill--permission {
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  color: #065f46;
}

.policy-card-pill--prohibition {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.policy-card-pill--obligation {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.policy-card-description {
  margin: 0 0 0.7rem;
  color: #475569;
  font-size: 0.9rem;
}
.delete-icon {
  background: transparent;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: #9ca3af; /* neutral */
  transition: color 0.2s ease, transform 0.2s ease;
}

.delete-icon svg {
  width: 18px;
  height: 18px;
}

.delete-icon:hover {
  color: #dc2626; /* danger red */
  transform: scale(1.1);
}
</style>
